/**
 * 時間粒度 (time grain) の共通定義。
 *
 * backend は `{vendor}_time_grain` を源泉に `GET /v3/querybar` の `time_granularities` で
 * **その vendor が実際に持つ粒度**を返す (例: 分粒度の vendor なら `["one_min"]`)。
 * フロントは長らく day/week/month の 3 択をハードコードしており、分粒度の vendor でも
 * `one_day` を送っていた。ここを唯一の定義として、アダプタとピッカーで共有する。
 *
 * キーは backend の `TimeUnitType` の値 (`one_min` 等)。`grain_key` は `one_` を落としたもので
 * i18n キー `data-mgmt-grain-key-<grain_key>` と対応する。
 */

/** 粗→細の正準順序 (backend `GRAIN_KEYS_COARSE_TO_FINE` と同じ並び)。 */
export const TIME_GRAINS_COARSE_TO_FINE = [
  "one_year",
  "one_quarter_year",
  "one_month",
  "one_week",
  "one_day",
  "one_quarter_day",
  "one_hour",
  "one_min",
  "one_sec",
  "one_millisec",
  "one_microsec",
  "one_nanosec",
] as const;

export type TimeGrainKey = (typeof TIME_GRAINS_COARSE_TO_FINE)[number];

/** `one_quarter_day` → `quarter_day` (i18n キーの接尾辞)。 */
export const toGrainKey = (key: string): string => key.replace(/^one_/, "");

export const TIME_GRAIN_LABELS: Record<string, { en: string; ja: string }> = {
  one_year: { en: "Yearly", ja: "年次" },
  one_quarter_year: { en: "Quarterly", ja: "四半期次" },
  one_month: { en: "Monthly", ja: "月次" },
  one_week: { en: "Weekly", ja: "週次" },
  one_day: { en: "Daily", ja: "日次" },
  one_quarter_day: { en: "6-hourly", ja: "6時間次" },
  one_hour: { en: "Hourly", ja: "時次" },
  one_min: { en: "Minutely", ja: "分次" },
  one_sec: { en: "Secondly", ja: "秒次" },
  one_millisec: { en: "Millisecond", ja: "ミリ秒次" },
  one_microsec: { en: "Microsecond", ja: "マイクロ秒次" },
  one_nanosec: { en: "Nanosecond", ja: "ナノ秒次" },
};

/** backend 由来の粒度リストを粗→細に整列する (未知キーは末尾に原順で残す)。 */
export const sortGrainsCoarseToFine = (keys: string[]): string[] => {
  const order = new Map<string, number>(
    TIME_GRAINS_COARSE_TO_FINE.map((k, i) => [k as string, i])
  );
  return [...keys].sort(
    (a, b) =>
      (order.get(a) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(b) ?? Number.MAX_SAFE_INTEGER)
  );
};

/** 最も細かい粒度。既定選択に使う (従来 day/week/month の既定が day = 最細だったのを踏襲)。 */
export const finestGrain = (keys: string[]): string | null => {
  const sorted = sortGrainsCoarseToFine(keys);
  return sorted.length ? sorted[sorted.length - 1] : null;
};

/** 日より細かいか (= 期間指定に時刻が要るか)。 */
export const isSubDaily = (key: string | null | undefined): boolean => {
  if (!key) return false;
  const idx = TIME_GRAINS_COARSE_TO_FINE.indexOf(key as TimeGrainKey);
  const dayIdx = TIME_GRAINS_COARSE_TO_FINE.indexOf("one_day");
  return idx > dayIdx;
};

/** 秒以下の粒度か (時刻入力に秒を出すか)。 */
export const needsSeconds = (key: string | null | undefined): boolean => {
  if (!key) return false;
  const idx = TIME_GRAINS_COARSE_TO_FINE.indexOf(key as TimeGrainKey);
  const secIdx = TIME_GRAINS_COARSE_TO_FINE.indexOf("one_sec");
  return idx >= secIdx;
};

/**
 * `Date` を **タイムゾーンをずらさずに** `YYYY-MM-DDTHH:mm:ss` へ整形する。
 *
 * ⚠ 旧 `formatUTCtoJSTWithoutTZ` は送信前に無条件で +9 時間していた。backend が返す
 * `datetime` は DWH のナイーブ値 (vendor のタイムライン) で UTC ではないため二重変換になり、
 * 終端 `2023-11-07 22:39` が `2023-11-08 07:39` へずれて日跨ぎし、日付に丸めた結果
 * 存在しない tick `2023-11-08 00:00` を送って 500 になっていた。
 */
export const formatDatetimeWithoutTZ = (value: Date | string): string => {
  const d = value instanceof Date ? value : new Date(value);
  const pad = (n: number, size = 2) => String(n).padStart(size, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};
